/**
 * /hr/calendar — Microsoft Graph shared-calendar connector.
 *
 * Wired to live endpoints:
 *   GET  /api/v1/hr/calendar/config   — enabled flag, mailbox, tenant, connected probe
 *   POST /api/v1/hr/calendar/test     — real create→delete round-trip on the mailbox
 *   GET  /api/v1/hr/calendar/activity — last 20 calendar_event.* audit rows
 *
 * Calendar has one behaviour (sync approved leave to one mailbox), so unlike the
 * Slack connector there is no event-routing table — just status + test + activity.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '../components/primitives/Card'
import { Icon } from '../components/primitives/Icon'
import { PageHead } from '../components/primitives/PageHead'
import { useToast } from '../components/primitives/Toast'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import {
  getCalendarConfig,
  getCalendarActivity,
  setCalendarSync,
  testCalendarConnection,
} from '../api/hr-calendar'

export function HrCalendarPage(): JSX.Element {
  return (
    <HrAccessGuard>
      <div className="page">
        <CalendarPageInner />
      </div>
    </HrAccessGuard>
  )
}

function CalendarPageInner(): JSX.Element {
  const queryClient = useQueryClient()
  const { notify } = useToast()

  const configQuery = useQuery({
    queryKey: ['hr-calendar-config'],
    queryFn: getCalendarConfig,
    retry: false,
  })
  const activityQuery = useQuery({
    queryKey: ['hr-calendar-activity'],
    queryFn: getCalendarActivity,
    retry: false,
  })

  const testMutation = useMutation({
    mutationFn: testCalendarConnection,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['hr-calendar-activity'] })
      notify(data.detail || 'Test passed — created and removed a test event.', 'success')
    },
    onError: (err) => {
      notify(err instanceof Error ? err.message : 'Test failed.', 'error')
    },
  })

  const syncMutation = useMutation({
    mutationFn: setCalendarSync,
    onSuccess: (_data, enabled) => {
      void queryClient.invalidateQueries({ queryKey: ['hr-calendar-config'] })
      notify(
        enabled
          ? 'Calendar connected — approved leave will sync.'
          : 'Calendar disconnected — approved leave will not sync.',
        'success',
      )
    },
    onError: (err) => {
      notify(err instanceof Error ? err.message : 'Could not change calendar sync.', 'error')
    },
  })

  const config = configQuery.data
  // Leave only actually syncs when all three are true: Graph reachable
  // (`connected`), the deploy capability is on (`capabilityEnabled`), and HR's
  // runtime switch is on (`syncEnabled`). Surface the *effective* state so the
  // page can't read "Connected" while sync silently can't fire.
  const syncEnabled = config?.syncEnabled ?? false
  const connected = config?.connected ?? false
  const capabilityEnabled = config?.capabilityEnabled ?? false
  const mailbox = config?.mailbox ?? null
  const status: { tone: 'good' | 'bad' | 'muted'; headline: string; text: string } = !connected
    ? {
        tone: 'bad',
        headline: 'Not connected',
        text: `Can't reach Microsoft Graph — check credentials and Calendars.ReadWrite consent${
          config?.detail ? ` (${config.detail})` : ''
        }`,
      }
    : !capabilityEnabled
      ? {
          tone: 'bad',
          headline: 'Sync disabled (deployment)',
          text: 'Graph is reachable, but sync is turned off at the deployment level — approved leave is NOT syncing.',
        }
      : !syncEnabled
        ? {
            tone: 'muted',
            headline: 'Sync switched off',
            text: 'Graph reachable and ready, but the sync switch is off — flip it on to start syncing.',
          }
        : {
            tone: 'good',
            headline: 'Connected — sync active',
            text: 'Approved leave is syncing to this calendar.',
          }
  const statusColor =
    status.tone === 'good' ? 'var(--success)' : status.tone === 'bad' ? 'var(--danger)' : 'var(--text-3)'

  return (
    <>
      <PageHead
        eyebrow="HR · Integrations"
        title="Calendar connector"
        subtitle="Approved leave is pushed to a shared Outlook calendar via Microsoft Graph."
      />

      {/* ── Connection card ───────────────────────────────────────── */}
      <Card padding={0} style={{ marginBottom: 16 }}>
        <div
          style={{
            padding: 22,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            gap: 18,
            alignItems: 'center',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: '#0F6CBD',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              fontSize: 28,
            }}
          >
            <Icon name="calendar_month" size={28} color="#fff" />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 17, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
                {mailbox ?? '— not configured —'}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                marginTop: 5,
                maxWidth: 560,
                fontWeight: 600,
                color: statusColor,
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: statusColor,
                  flexShrink: 0,
                }}
              />
              {status.headline}
            </div>
            <div style={{ fontSize: 12, marginTop: 3, maxWidth: 560, color: 'var(--text-3)' }}>
              {status.text}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <SyncSwitch
              checked={syncEnabled}
              disabled={syncMutation.isPending}
              onChange={(v) => syncMutation.mutate(v)}
            />
            {syncMutation.isPending ? (
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', textAlign: 'right' }}>Saving…</div>
            ) : null}
            <button
              type="button"
              onClick={() => testMutation.mutate()}
              disabled={
                !mailbox || !connected || !capabilityEnabled || !syncEnabled || testMutation.isPending
              }
              title={
                !mailbox
                  ? 'Mailbox not configured'
                  : !connected
                    ? "Can't reach Microsoft Graph"
                    : !capabilityEnabled
                      ? 'Sync is turned off at the deployment level'
                      : !syncEnabled
                        ? 'Turn the sync switch on to run a test'
                        : 'Create and delete a test event'
              }
              style={buttonPrimary}
            >
              {testMutation.isPending ? 'Testing…' : 'Test connection'}
            </button>
          </div>
        </div>

        <div
          style={{
            padding: '12px 22px',
            borderTop: '1px solid var(--border)',
            background: 'var(--surface-2)',
            fontSize: 12,
            color: 'var(--text-2)',
            lineHeight: 1.5,
          }}
        >
          Approved leave creates an all-day event; cancelling or rejecting removes it.
          The <strong>Test connection</strong> button creates then deletes a throwaway
          event to prove the live wiring without leaving anything behind.
        </div>
      </Card>

      {/* ── Recent activity ───────────────────────────────────────── */}
      <Card padding={0}>
        <div style={{ padding: '18px 22px' }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Recent activity</div>
          <div style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 2 }}>
            Last 20 calendar sync events
          </div>
        </div>

        {activityQuery.isLoading ? (
          <Pad>Loading…</Pad>
        ) : activityQuery.isError ? (
          <Pad tone="bad">
            {activityQuery.error instanceof Error
              ? activityQuery.error.message
              : 'Failed to load activity.'}
          </Pad>
        ) : !activityQuery.data || activityQuery.data.rows.length === 0 ? (
          <Pad>No calendar activity yet.</Pad>
        ) : (
          <div>
            {activityQuery.data.rows.map((row, i) => (
              <div
                key={i}
                style={{
                  padding: '10px 22px',
                  borderTop: '1px solid var(--border)',
                  fontSize: 12.5,
                  display: 'flex',
                  gap: 10,
                  alignItems: 'baseline',
                }}
              >
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-3)', fontSize: 11, minWidth: 72 }}>
                  {row.when}
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10.5,
                    padding: '1px 6px',
                    borderRadius: 4,
                    background: 'var(--surface-2)',
                    color: 'var(--text-2)',
                  }}
                >
                  {row.action}
                </span>
                <span style={{ color: 'var(--text-2)' }}>{row.detail}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  )
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function SyncSwitch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean
  disabled?: boolean
  onChange: (v: boolean) => void
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label="Connect or disconnect calendar sync"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      data-testid="calendar-sync-toggle"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 999,
        border: '1px solid var(--border)',
        background: checked ? 'var(--success-tint)' : 'var(--surface-2)',
        color: checked ? 'var(--success)' : 'var(--text-2)',
        fontWeight: 600,
        fontSize: 12.5,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {checked ? 'Connected' : 'Disconnected'}
      <span
        aria-hidden
        style={{
          width: 34,
          height: 18,
          borderRadius: 999,
          background: checked ? 'var(--success)' : 'var(--text-3)',
          position: 'relative',
          transition: 'background 150ms',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 18 : 2,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left 150ms',
          }}
        />
      </span>
    </button>
  )
}

function Pad({ children, tone }: { children: React.ReactNode; tone?: 'bad' }): JSX.Element {
  return (
    <div
      role={tone === 'bad' ? 'alert' : undefined}
      style={{
        padding: '24px 22px',
        fontSize: 13,
        color: tone === 'bad' ? 'var(--danger)' : 'var(--text-3)',
      }}
    >
      {children}
    </div>
  )
}

const buttonPrimary: React.CSSProperties = {
  padding: '7px 14px',
  borderRadius: 8,
  border: 'none',
  background: 'var(--steel-blue)',
  color: 'var(--surface)',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
}
